import type { ComponentType } from 'react';
import type { SimplePanelProps } from './adapt';
import HotelPanel from './HotelPanel';
import CateringPanel from './CateringPanel';
import DuringDietary from './DuringDietary';
import EventbritePanel from './EventbritePanel';
import PheedloopPanel from './PheedloopPanel';
import PheedloopCheckinUpload from './PheedloopCheckinUpload';
import { gated } from './pageContext';
import AgendaPanel from './AgendaPanel';
import SpeakersPanel from './SpeakersPanel';
import AgendaOutputs from './AgendaOutputs';
import DayRunOfShow from './DayRunOfShow';
import SubEventsPanel from './SubEventsPanel';
import AgendaImportPanel from './AgendaImportPanel';
import CountsPanel from './CountsPanel';
import BudgetPanel from './money/BudgetPanel';
import VendorsPanel from './money/VendorsPanel';
import VendorDeadlinesPanel from './money/VendorDeadlinesPanel';
import GiftCardRequestsPanel from './money/GiftCardRequestsPanel';
import TreasuryPanel from './money/TreasuryPanel';
import { ReimbursementSteps, HonorariaSteps } from './money/YorkStepsPanel';
import {
  TicketsPanel,
  RegistrantProgressPanel,
  OutreachPanel,
  CalendarInvitesPanel,
  CompsPanel,
  PartnersPanel,
} from './registration/RegistrationPanels';
import { CompetitionsPanel, DignitariesPanel, InterpretationPanel, RecordingsPanel } from './ProgramPanels';
import InfoPackagesPanel from './info/InfoPackagesPanel';
import CommsPlanPanel from './info/CommsPlanPanel';
import SessionFeedbackPanel from './info/SessionFeedbackPanel';
import StatsExtrasPanel from './info/StatsExtrasPanel';
import {
  AccommodationsPanel,
  ArrivalsPanel,
  DayOfToday,
  GroupsPanel,
  ParkingPanel,
  StaffRosterPanel,
  TravelExceptionsPanel,
} from './DayOfPanels';

// Self-contained panels ({ eventId, canEdit, day? }) shown under a stage tab, in order.
// Wire one with an import line and its name in the tab's list, e.g.
//   import SpeakersPanel from './SpeakersPanel'   →   'before.speakers': [SpeakersPanel],
// Keys: '<stage>.<tab>' (tabs in stages.ts), plus 'during.agenda' (top of each day,
// gets `day`), 'during.dietary' (replaces the day's dietary list) and 'during.day' (after the
// dietary list, gets `day`).
export const EXTRAS: Record<string, ComponentType<SimplePanelProps>[]> = {
  'before.overview': [SubEventsPanel],
  'before.registration': [
    RegistrantProgressPanel,
    gated('before.registration.tickets', TicketsPanel),
    EventbritePanel,
    gated('before.registration.calendar', CalendarInvitesPanel),
    gated('before.registration.outreach', OutreachPanel),
    gated('before.registration.comps', CompsPanel),
    gated('before.registration.partners', PartnersPanel),
  ],
  'before.speakers': [
    gated('before.speakers.import', AgendaImportPanel),
    AgendaPanel,
    SpeakersPanel,
    gated('before.speakers.interpretation', InterpretationPanel),
    gated('before.speakers.dignitaries', DignitariesPanel),
    gated('before.speakers.competitions', CompetitionsPanel),
  ],
  'before.hotel': [HotelPanel],
  'before.catering': [CateringPanel],
  'before.documents': [InfoPackagesPanel, AgendaOutputs, gated('before.documents.pheedloop', PheedloopPanel)],
  'before.emails': [CommsPlanPanel],
  'during.agenda': [DayRunOfShow],
  'during.dietary': [DuringDietary],
  'during.day': [DayOfToday],
  'before.logistics': [
    gated('before.logistics.staff', StaffRosterPanel),
    gated('before.logistics.groups', GroupsPanel),
    gated('before.logistics.parking', ParkingPanel),
    ArrivalsPanel,
    AccommodationsPanel,
    TravelExceptionsPanel,
  ],
  'before.budget': [
    BudgetPanel,
    gated('before.budget.vendors', VendorsPanel),
    gated('before.budget.giftcards', GiftCardRequestsPanel),
    gated('before.budget.treasury', TreasuryPanel),
  ],
  'before.timeline': [VendorDeadlinesPanel],
  'after.attendance': [gated('after.attendance.pheedloop', PheedloopCheckinUpload), CountsPanel],
  'after.reimbursements': [gated('after.reimbursements.steps', ReimbursementSteps), TravelExceptionsPanel],
  'after.honoraria': [
    gated('after.honoraria.steps', HonorariaSteps),
    gated('after.honoraria.giftcards', GiftCardRequestsPanel),
  ],
  'after.budget': [
    BudgetPanel,
    gated('after.budget.vendors', VendorsPanel),
    gated('after.budget.treasury', TreasuryPanel),
  ],
  'after.recordings': [RecordingsPanel],
  'after.feedback': [gated('after.feedback.sessions', SessionFeedbackPanel)],
  'after.report': [StatsExtrasPanel],
};

export const extrasFor = (key: string) => EXTRAS[key] ?? [];
